/**
 * Utility functions for handling email webhook events
 */

import type {
  EmailReceivedPayload,
  EmailOpenedPayload,
  EmailLinkClickedPayload,
} from "@/dtos/EmailWebhook.dto";

/**
 * Find email in list by ID (checks both id and provider_id)
 */
export function findEmailInList(
  emails: any[],
  emailId: string
): { email: any; index: number } | null {
  const index = emails.findIndex((email) => email.id === emailId || email.provider_id === emailId);
  if (index === -1) return null;
  return { email: emails[index], index };
}

/**
 * Update email read status in list
 */
export function updateEmailReadStatus(
  emails: any[],
  emailId: string,
  readAt: string | null
): boolean {
  const result = findEmailInList(emails, emailId);
  if (result) {
    result.email.read_date = readAt;
    return true;
  }
  return false;
}

/**
 * Add new email to list (prepend to beginning)
 */
export function addEmailToList(emails: any[], newEmail: any): void {
  // Check if email already exists
  const exists = emails.some(
    (email) => email.id === newEmail.id || email.provider_id === newEmail.provider_id
  );
  if (!exists) {
    emails.unshift(newEmail);
  }
}

/**
 * Format notification message for email received
 */
export function formatEmailReceivedMessage(data: EmailReceivedPayload): string {
  const { senderEmail, subject } = data.payload;
  if (subject) {
    return `New email from ${senderEmail}\nSubject: ${subject}`;
  }
  return `New email from ${senderEmail}`;
}

/**
 * Format notification message for email opened
 */
export function formatEmailOpenedMessage(data: EmailOpenedPayload): string {
  const { recipientEmail } = data.payload;
  return `${recipientEmail} opened your email`;
}

/**
 * Format notification message for link clicked
 */
export function formatEmailLinkClickedMessage(data: EmailLinkClickedPayload): string {
  const { recipientEmail, url, label } = data.payload;
  const linkText = label || url || "a link";
  return `${recipientEmail} clicked: ${linkText}`;
}

/**
 * Check if email belongs to current account/channel
 */
export function belongsToAccount(
  payload: { accountId?: string },
  currentAccountId?: string
): boolean {
  if (!currentAccountId || !payload.accountId) return false;
  return payload.accountId === currentAccountId;
}
